import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getResources, createResource, deleteResource, updateResource, getResourceFolders, createResourceFolder, API_BASE } from "../lib/api";
import { ResourceFolderSection } from "../components/resources/ResourceFolderSection";
import { UploadSlidesDialog } from "../components/resources/UploadSlidesDialog";
import { AnnounceDialog } from "../components/resources/AnnounceDialog";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Plus, Trash2, Loader2, BookOpen, Presentation, GraduationCap, Calculator, Wand2, Phone, Sparkles, LayoutGrid, FolderPlus, Upload, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORIES = [
  { value: "sales_deck", label: "Sales Deck", icon: Presentation, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "training", label: "Training & Skills", icon: GraduationCap, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "other", label: "Other", icon: BookOpen, color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
];

export default function ResourcesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sales_deck");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", description: "", category: "sales_deck" });
  const [submitting, setSubmitting] = useState(false);
  const [folders, setFolders] = useState([]);
  const [uploadFolder, setUploadFolder] = useState(null);
  const [announceFolder, setAnnounceFolder] = useState(undefined);

  const load = (initial = false) => {
    if (initial) setLoading(true);
    Promise.all([getResources(), getResourceFolders()])
      .then(([r, f]) => { setResources(r.data || []); setFolders(f.data || []); })
      .finally(() => setLoading(false));
  };

  const newFolder = async () => {
    const name = window.prompt("New folder name");
    if (!name?.trim()) return;
    try {
      await createResourceFolder({ name: name.trim(), category: activeTab });
      toast.success("Folder created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create folder"); }
  };

  const moveTo = async (id, folderId) => {
    try {
      await updateResource(id, { folder_id: folderId === "none" ? null : folderId });
      toast.success("Moved");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not move"); }
  };
  useEffect(() => { load(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    setSubmitting(true);
    try {
      await createResource(form);
      toast.success("Resource added");
      setForm({ title: "", url: "", description: "", category: activeTab });
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await deleteResource(id);
      toast.success("Resource removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const filtered = resources.filter((r) => r.category === activeTab);
  const tabFolders = folders.filter((f) => (f.category || "sales_deck") === activeTab);
  const folderIds = new Set(tabFolders.map((f) => f.id));
  const unfiled = filtered.filter((r) => !r.folder_id || !folderIds.has(r.folder_id));
  const hrefFor = (url) => (url?.startsWith("/api/") ? `${API_BASE}${url}` : url);

  const renderCard = (r) => {
    const cat = CATEGORIES.find((c) => c.value === r.category) || CATEGORIES[2];
    const Icon = cat.icon;
    return (
      <Card key={r.id} className="border hover:shadow-md transition" data-testid={`resource-card-${r.id}`}>
        <CardContent className="pt-5">
          <div className="flex items-start justify-between gap-2">
            <div className={`p-2 rounded-lg border ${cat.color} shrink-0`}><Icon className="h-4 w-4" /></div>
            {isAdmin && (
              <div className="flex items-center gap-1">
                <Select value={r.folder_id || "none"} onValueChange={(v) => moveTo(r.id, v)}>
                  <SelectTrigger className="h-7 text-xs w-[180px]" data-testid={`resource-move-${r.id}`}><SelectValue placeholder="Move to…" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unfiled</SelectItem>{folders.filter((f) => (f.category || "sales_deck") === r.category).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="h-7 w-7 p-0" data-testid={`resource-delete-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </Button>
              </div>
            )}
          </div>
          <h3 className="font-semibold mt-3 text-sm">{r.title}</h3>
          {r.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</p>}
          <a href={hrefFor(r.url)} target="_blank" rel="noopener noreferrer" data-testid={`resource-open-${r.id}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary mt-3 hover:underline">
            {r.original_filename ? "Download" : "Open"} <ExternalLink className="h-3 w-3" />
          </a>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Resource Center</h1>
          <p className="text-muted-foreground mt-1">Sales decks, training, and reference materials curated for the team.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={newFolder} data-testid="new-folder-btn"><FolderPlus className="h-4 w-4 mr-2" />New Folder</Button>
          <Button variant="outline" onClick={() => setAnnounceFolder(null)} data-testid="notify-partners-btn"><Megaphone className="h-4 w-4 mr-2" />Notify Partners</Button>
          <Button onClick={() => setUploadFolder("")} className="bg-amber-500 hover:bg-amber-600" data-testid="upload-slides-btn"><Upload className="h-4 w-4 mr-2" />Upload Slides</Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-resource-btn"><Plus className="h-4 w-4 mr-2" /> Add Resource</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Resource</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title" /></div>
                <div><Label>URL (Google Drive, YouTube, Loom, etc.)</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="resource-url" /></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="resource-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {folders.some((f) => (f.category || "sales_deck") === form.category) && <div><Label>Folder (optional)</Label>
                  <Select value={form.folder_id || "none"} onValueChange={(v) => setForm({ ...form, folder_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="resource-folder"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No folder</SelectItem>{folders.filter((f) => (f.category || "sales_deck") === form.category).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>}
                <div><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button onClick={submit} disabled={submitting} data-testid="resource-save">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>
      {isAdmin && <>
        <UploadSlidesDialog open={uploadFolder !== null} onOpenChange={(v) => !v && setUploadFolder(null)}
          folders={tabFolders} category={activeTab} defaultFolderId={uploadFolder} onDone={load} />
        <AnnounceDialog open={announceFolder !== undefined} onOpenChange={(v) => !v && setAnnounceFolder(undefined)}
          folder={announceFolder} />
      </>}

      {/* Quick "Get your branded deck" CTA — appears on the Sales Deck tab only */}
      {activeTab === "sales_deck" && (
        <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardContent className="py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="bg-indigo-600 text-white rounded-lg p-2.5">
                <Presentation className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Your branded ProspectMax deck</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                  Includes your agency logo on the cover, your agency&apos;s AI voice demo phone injected on slides 4, 6 &amp; 14,
                  the embedded Excel ROI calculator preserved, a QR code linking to the live portal ROI calculator,
                  and your personal contact slide at the end.
                </p>
              </div>
            </div>
            <Button asChild className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="download-branded-prospectmax">
              <a href={`${API_BASE}/api/decks/prospectmax`} download>
                Download Branded Deck
              </a>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ROI Estimator — internal rep tool. Lives on the Sales Deck tab so it
          sits next to the branded ProspectMax deck. Lets reps run a live "missed
          opportunity" estimate during a prospect conversation, email the prospect
          a co-branded PDF, OR download a ProspectMax deck with the prospect's own
          numbers already injected on slide 15. */}
      {activeTab === "sales_deck" && (
        <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardContent className="py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="bg-emerald-600 text-white rounded-lg p-2.5">
                <Calculator className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Missed Opportunity ROI Estimator</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                  Run a live estimate based on your prospect&apos;s missed calls + web leads, close rate, and customer
                  LTV. Email them a co-branded PDF, or drop their actual numbers into a ProspectMax deck and pick up
                  the conversation right where you left off.
                </p>
              </div>
            </div>
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="open-roi-estimator">
              <Link to="/resources/roi-estimator">Open ROI Estimator</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Slide Library picker — reps browse every master-deck slide, tick the
          ones they want, and download a custom PPTX. Sits on the Sales Deck
          tab beside the other pitch tools. */}
      {activeTab === "sales_deck" && (
        <Card className="border-2 border-sky-200 bg-gradient-to-r from-sky-50 to-cyan-50">
          <CardContent className="py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="bg-sky-600 text-white rounded-lg p-2.5">
                <LayoutGrid className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Slide Library — Pick &amp; Download</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                  Browse every slide across our master decks (ProspectID, ProspectMax, SEO/AEO, Buyer
                  Intent SEM, and more). Pick the exact slides you need, and download them together
                  as a single custom PPTX — perfect for one-off pitches or dropping into an email.
                </p>
              </div>
            </div>
            <Button asChild className="bg-sky-600 hover:bg-sky-700 shrink-0" data-testid="open-slide-library-picker">
              <Link to="/resources/slide-library">Open Slide Library</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Homepage Mockup Builder — Claude Sonnet 4.5 drafts a self-contained
          HTML hero page for a prospect. Reps share the live link, download a
          PDF, or drop the PNG into a proposal slide deck. */}
      {activeTab === "sales_deck" && (
        <Card className="border-2 border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-indigo-50">
          <CardContent className="py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="bg-gradient-to-br from-fuchsia-600 to-indigo-600 text-white rounded-lg p-2.5">
                <Wand2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Homepage Mockup Builder — Claude AI</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                  On the fly, show a prospect what their modernized homepage could look like. Fill in a few details,
                  Claude drafts a live hero-page mockup in ~60–90s. Share the public link, download the PDF, or drop
                  the PNG into your pitch deck.
                </p>
              </div>
            </div>
            <Button
              asChild
              className="bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700 shrink-0"
              data-testid="open-mockup-builder-resources"
            >
              <Link to="/mockups">Open Mockup Builder</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Practice-with-Sandy AI voice — appears on the Training tab.
          Reps call this number to run a live sales rehearsal against an
          AI Voice Agent that plays the prospect role: needs-analysis,
          product pitch, objection handling, and close. */}
      {activeTab === "training" && (
        <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-pink-50" data-testid="sandy-practice-card">
          <CardContent className="py-5">
            <div className="flex items-start gap-5 flex-wrap">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_partner-hub-115/artifacts/virf1qtd_SuperAi%20Agent.png"
                alt="Sandy — Super AI Voice Agent"
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain rounded-2xl bg-white shadow-md ring-2 ring-purple-300 shrink-0"
                data-testid="sandy-avatar"
              />
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-outfit font-bold text-lg">Practice with Sandy — our AI Voice Agent</h3>
                  <span className="text-[10px] font-bold tracking-widest uppercase bg-purple-600 text-white px-2 py-0.5 rounded">Free</span>
                </div>
                <p className="text-sm text-slate-700 mt-1 max-w-2xl">
                  Sharpen your pitch by role-playing a full sales conversation with Sandy — anytime, day or night.
                  She&apos;ll play the prospect, and it&apos;s on you to run the call: share the client, uncover needs,
                  pitch the right product, handle objections, and close (or book the next call).
                </p>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <a
                    href="tel:+17275917122"
                    className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2.5 rounded-lg text-base transition"
                    data-testid="sandy-call-btn"
                  >
                    <Phone className="h-4 w-4" /> (727) 591-7122
                  </a>
                  <span className="text-xs text-slate-500">Tap to call from mobile · or dial from any phone</span>
                </div>
                <div className="mt-4 rounded-lg bg-white/70 border border-purple-200 p-3">
                  <p className="text-xs font-bold tracking-widest uppercase text-purple-700 mb-2">How the call goes</p>
                  <ol className="text-sm text-slate-700 space-y-1.5 list-decimal list-inside">
                    <li><span className="font-medium">Tell Sandy who you&apos;re pitching</span> — client name and industry (e.g. &ldquo;roofing company in Tampa&rdquo;).</li>
                    <li><span className="font-medium">Run the needs analysis</span> — ask about her business, current marketing, pain points.</li>
                    <li><span className="font-medium">Pitch the right product</span> based on what she tells you (ProspectID, ProspectMax, SEO/AEO, or a website).</li>
                    <li><span className="font-medium">Handle her objections</span> — price, timing, &ldquo;let me think about it&rdquo;.</li>
                    <li><span className="font-medium">Close</span> the deal or lock in the next call.</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 border-b">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const count = resources.filter((r) => r.category === c.value).length;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setActiveTab(c.value)}
              data-testid={`tab-${c.value}`}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-medium ${activeTab === c.value ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="h-4 w-4" />
              {c.label}
              {count > 0 && <span className="text-xs bg-muted px-1.5 rounded">{count}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 && tabFolders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No resources here yet.</p>
            {isAdmin && <p className="text-xs mt-1">Click &ldquo;Add Resource&rdquo; above to upload your first link.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="resource-folders-list">
          {tabFolders.map((f, i) => (
            <ResourceFolderSection key={f.id} folder={f} defaultOpen={i === 0} isAdmin={isAdmin} onChanged={load}
              items={filtered.filter((r) => r.folder_id === f.id)} renderCard={renderCard}
              onUpload={(id) => setUploadFolder(id)} onAnnounce={(fo) => setAnnounceFolder(fo)} />
          ))}
          {unfiled.length > 0 && (tabFolders.length > 0
            ? <ResourceFolderSection folder={null} defaultOpen items={unfiled} isAdmin={isAdmin} renderCard={renderCard} onChanged={load} />
            : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{unfiled.map(renderCard)}</div>)}
        </div>
      )}
    </div>
  );
}
